// Rangreza home sections: carousel arrows, quick add, sticky mobile CTA.
// Loaded as a module (deferred, and runs once even when several sections include it).

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initCarousel(root) {
  const track = root.querySelector('[data-rr-track]');
  const prev = root.querySelector('[data-rr-prev]');
  const next = root.querySelector('[data-rr-next]');
  if (!track || !prev || !next) return;

  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max;
  };
  const go = (dir) => {
    track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  prev.addEventListener('click', () => go(-1));
  next.addEventListener('click', () => go(1));
  track.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(track);
  update();
}

function closePanels(except) {
  document.querySelectorAll('.rr-qa__toggle[aria-expanded="true"]').forEach((btn) => {
    if (btn === except) return;
    btn.setAttribute('aria-expanded', 'false');
    btn.closest('.rr-qa')?.classList.remove('is-open');
  });
}

async function addToCart(button) {
  const qa = button.closest('[data-rr-qa]');
  const status = qa?.querySelector('.rr-qa__status');
  qa?.classList.add('is-loading');
  button.classList.add('is-loading');
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.rrQaAdd), quantity: 1 }] })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Error');
    if (status) status.textContent = qa.dataset.addedLabel || '';
    qa?.classList.add('is-added');
    closePanels();
    // Impulse rebuilds and opens its cart drawer on these events.
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    setTimeout(() => qa?.classList.remove('is-added'), 2000);
  } catch (err) {
    if (status) status.textContent = err.message;
    status?.classList.remove('visually-hidden');
    setTimeout(() => status?.classList.add('visually-hidden'), 4000);
  } finally {
    qa?.classList.remove('is-loading');
    button.classList.remove('is-loading');
  }
}

function initQuickAdd() {
  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-rr-qa-add]');
    if (add) {
      event.preventDefault();
      addToCart(add);
      return;
    }
    const toggle = event.target.closest('.rr-qa__toggle[aria-controls]');
    if (toggle) {
      event.preventDefault();
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      closePanels(toggle);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.closest('.rr-qa')?.classList.toggle('is-open', open);
      if (open) toggle.nextElementSibling?.querySelector('button:not([disabled])')?.focus();
      return;
    }
    if (!event.target.closest('.rr-qa')) closePanels();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.rr-qa__toggle[aria-expanded="true"]');
    if (open) {
      closePanels();
      open.focus();
    }
  });
}

function initSticky(bar) {
  const offset = Number(bar.dataset.offset) || 600;
  const footer = document.querySelector('footer, .site-footer, #shopify-section-footer');
  let footerVisible = false;
  bar.hidden = false;
  const update = () => {
    bar.classList.toggle('is-visible', window.scrollY > offset && !footerVisible);
  };
  if (footer) {
    new IntersectionObserver((entries) => {
      footerVisible = entries[0].isIntersecting;
      update();
    }).observe(footer);
  }
  window.addEventListener('scroll', update, { passive: true });
  update();
}

// Brand ribbon: arrows always work; when a speed is set it drifts slowly and loops
// (the list is rendered twice, so jumping by one copy's width is invisible).
// Pauses on hover, focus, touch, after an arrow press, off screen, or via its button.
function initRibbon(root) {
  const track = root.querySelector('[data-rr-ribbon-track]');
  const copy = track && track.querySelector('ul');
  if (!copy) return;
  const prev = root.querySelector('[data-rr-ribbon-prev]');
  const next = root.querySelector('[data-rr-ribbon-next]');
  const toggle = root.querySelector('[data-rr-ribbon-toggle]');
  const dir = root.dataset.direction === 'left' ? 1 : -1; // -1: content travels left to right
  const secondsPerBrand = Number(root.dataset.speed) || 0;
  const count = Number(root.dataset.count) || copy.children.length || 1;
  const auto = secondsPerBrand > 0 && !reduceMotion;

  const half = () => copy.offsetWidth;
  const maxScroll = () => track.scrollWidth - track.clientWidth;
  // Only jump (by one copy) at the real ends, so smooth scrolls in between aren't interrupted.
  const wrap = (x) => {
    const w = half();
    if (w <= track.clientWidth) return x;
    if (x < 1) return x + w;
    if (x > maxScroll() - 1) return x - w;
    return x;
  };

  let pos = dir === -1 ? half() : 0;
  track.scrollLeft = pos;

  let hovered = false;
  let focused = false;
  let userPaused = false;
  let visible = true;
  let holdUntil = 0;
  let last = 0;
  const running = () => auto && !hovered && !focused && !userPaused && visible && performance.now() > holdUntil;
  const hold = (ms) => { holdUntil = performance.now() + ms; };

  // Keep manual scrolling (swipe, arrows) endless too.
  track.addEventListener('scroll', () => {
    if (running()) return;
    const x = wrap(track.scrollLeft);
    if (x !== track.scrollLeft) track.scrollLeft = x;
    pos = track.scrollLeft;
  }, { passive: true });

  const step = (sign) => {
    hold(5000);
    const amount = track.clientWidth * 0.8 * sign;
    const target = track.scrollLeft + amount;
    // Jump by one copy first if the smooth scroll would run off either end.
    if (target < 1) track.scrollLeft += half();
    else if (target > maxScroll() - 1) track.scrollLeft -= half();
    track.scrollBy({ left: amount, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  prev?.addEventListener('click', () => step(-1));
  next?.addEventListener('click', () => step(1));

  if (!auto) return;

  root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hovered = true; });
  root.addEventListener('pointerleave', () => { hovered = false; });
  // Keyboard focus pauses; a mouse or touch click on an arrow only holds briefly (see step).
  root.addEventListener('focusin', (e) => { focused = e.target.matches(':focus-visible'); });
  root.addEventListener('focusout', () => { focused = false; });
  track.addEventListener('touchstart', () => hold(4000), { passive: true });
  track.addEventListener('wheel', () => hold(4000), { passive: true });

  if (toggle) {
    toggle.hidden = false;
    toggle.addEventListener('click', () => {
      userPaused = !userPaused;
      toggle.setAttribute('aria-pressed', String(userPaused));
    });
  }

  new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
  }).observe(root);

  const tick = (now) => {
    const dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
    last = now;
    if (running()) {
      const pxPerSecond = half() / count / secondsPerBrand;
      pos = wrap(pos + dir * pxPerSecond * dt);
      track.scrollLeft = pos;
    } else {
      pos = track.scrollLeft;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

document.querySelectorAll('[data-rr-carousel]').forEach(initCarousel);
initQuickAdd();
document.querySelectorAll('[data-rr-sticky]').forEach(initSticky);
document.querySelectorAll('[data-rr-ribbon]').forEach(initRibbon);

// Sections added or changed in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-rr-carousel]').forEach(initCarousel);
  event.target.querySelectorAll('[data-rr-sticky]').forEach(initSticky);
  event.target.querySelectorAll('[data-rr-ribbon]').forEach(initRibbon);
});
